import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import WellForm from './WellForm';

interface Well {
  id: string;
  uwi: string;
  lease: string;
  wellNumber: string;
  status: string;
  wellType: string;
  county: string;
  state: string;
  country: string;
  company: string;
  latitude: number;
  longitude: number;
  name: string;
}

interface TimelineItem {
  id: string;
  user: string;
  type: string;
  content: string;
  timestamp: string;
}

const WellDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [well, setWell] = useState<Well | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [showForm, setShowForm] = useState(false);

  const fetchWell = async () => {
    if (!id) return;
    const res = await api.wells.getById(id);
    const w = res.data.data;
    const mapped: Well = {
      id: w.id,
      uwi: w.uwi,
      lease: w.lease,
      wellNumber: w.well_number ?? w.wellNumber,
      status: w.status,
      wellType: w.well_type ?? w.wellType,
      county: w.county,
      state: w.state,
      country: w.country,
      company: w.company,
      latitude: w.latitude,
      longitude: w.longitude,
      name: `${w.lease ?? ''} ${w.well_number ?? w.wellNumber ?? ''}`.trim(),
    };
    setWell(mapped);
  };

  const fetchTimeline = async () => {
    if (!id) return;
    const res = await api.activities.getTimeline(id, 30);
    const timelineData = (res.data?.data || []).map((t: any) => ({
      id: t.id,
      user: `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.trim(),
      type: t.type,
      content: t.description || t.activity,
      timestamp: new Date(t.createdAt).toLocaleString(),
    }));
    setTimeline(timelineData);
  };

  useEffect(() => {
    fetchWell();
    fetchTimeline();
  }, [id]);

  const handleDelete = async () => {
    if (!id || !well) return;
    if (window.confirm(`Delete ${well.name}?`)) {
      await api.wells.delete(id);
      navigate('/admin');
    }
  };

  return (
    <div className="space-y-6 text-white p-6">
      {well && (
        <>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">{well.name}</h1>
            <div className="space-x-2">
              <button
                onClick={() => setShowForm(true)}
                className="px-3 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors inline-flex items-center"
              >
                <Edit className="w-4 h-4 mr-1" /> Edit
              </button>
              <button
                onClick={handleDelete}
                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors inline-flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 bg-gray-900 p-4 rounded-lg">
            <div><span className="text-gray-400">UWI:</span> {well.uwi}</div>
            <div><span className="text-gray-400">Lease:</span> {well.lease}</div>
            <div><span className="text-gray-400">Well Number:</span> {well.wellNumber}</div>
            <div><span className="text-gray-400">Status:</span> {well.status}</div>
            <div><span className="text-gray-400">Well Type:</span> {well.wellType}</div>
            <div><span className="text-gray-400">County:</span> {well.county}</div>
            <div><span className="text-gray-400">State:</span> {well.state}</div>
            <div><span className="text-gray-400">Country:</span> {well.country}</div>
            <div><span className="text-gray-400">Company:</span> {well.company}</div>
            <div><span className="text-gray-400">Latitude:</span> {well.latitude}</div>
            <div><span className="text-gray-400">Longitude:</span> {well.longitude}</div>
          </div>

          <div className="bg-gray-900 p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Timeline</h2>
            {timeline.length === 0 && (
              <p className="text-gray-400">No activity.</p>
            )}
            <ul className="space-y-4">
              {timeline.map(item => (
                <li key={item.id} className="border-b border-gray-800 pb-2">
                  <p className="text-sm text-gray-400">{item.timestamp} - {item.user}</p>
                  <p className="text-white">{item.content}</p>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {showForm && well && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center">
          <div className="bg-gray-900 p-4 rounded w-full max-w-lg">
            <div className="flex justify-between mb-4">
              <h2>Edit Well</h2>
              <button onClick={() => setShowForm(false)}>X</button>
            </div>
            <WellForm initialWell={well} onSaved={() => { setShowForm(false); fetchWell(); }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default WellDetails;
